import React, { useState, useEffect, useRef, useContext } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  MenuItem,
  Menu,
  InputBase,
  Box,
  Badge,
  Fade,
  Tooltip,
  Button,
  Chip,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Mail as MailIcon,
  Search as SearchIcon,
  AccountCircle,
  Business,
  ContactPhone,
  AlternateEmail,
  Badge as BadgeIcon,
  PersonOutline,
  Contacts,
  Close,
} from "@mui/icons-material";
import { UserContext } from "../Context/Context";
import api from "../../api/axios";
import { useUnreadMessages } from "../helpers/Echo/useUnreadMessages";

const ResultRow = ({ result, type, onClick, href }) => {
  const isCliente = type === "Cliente";
  const accentColor = isCliente ? "#1976d2" : "#7b1fa2";

  return (
    <Box
      component="a"
      href={href}
      onClick={(e) => {
        if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
          e.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        px: 2,
        py: 1.2,
        cursor: "pointer",
        borderBottom: "1px solid rgba(148,163,184,0.12)",
        transition: "background 0.15s",
        textDecoration: "none",
        color: "inherit",
        background: "rgba(15, 23, 42, 0.2)",
        "&:hover": { bgcolor: "rgba(96,165,250,0.12)" },
        "&:last-child": { borderBottom: "none" },
      }}
    >
      {/* Avatar inicial */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: isCliente ? "#e3f2fd" : "#f3e5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: 14,
          fontWeight: 700,
          color: accentColor,
        }}
      >
        {(result.name?.[0] ?? "?").toUpperCase()}
      </Box>

      {/* Datos */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontWeight: 600,
            fontSize: 14,
            color: "#f8fafc",
            lineHeight: 1.3,
          }}
        >
          {result.name} {result.surname}
        </Box>
        <Box
          sx={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", mt: 0.4 }}
        >
          {result.phone && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                fontSize: 12,
                color: "#cbd5e1",
              }}
            >
              <ContactPhone sx={{ fontSize: 13, color: "#90a4ae" }} />
              {result.phone}
            </Box>
          )}
          {result.nif && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                fontSize: 12,
                color: "#cbd5e1",
              }}
            >
              <BadgeIcon sx={{ fontSize: 13, color: "#90a4ae" }} />
              {result.nif}
            </Box>
          )}
          {result.email && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                fontSize: 12,
                color: "#cbd5e1",
                minWidth: 0,
              }}
            >
              <AlternateEmail
                sx={{ fontSize: 13, color: "#90a4ae", flexShrink: 0 }}
              />
              <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {result.email}
              </Box>
            </Box>
          )}
          {result?.company_relation?.company_name && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                fontSize: 12,
                color: "#cbd5e1",
              }}
            >
              <Business sx={{ fontSize: 13, color: "#90a4ae" }} />
              {result.company_relation.company_name}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Header = ({ handleToggleSidebar }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [mailAnchorEl, setMailAnchorEl] = useState(null);
  const openMailMenu = Boolean(mailAnchorEl);
  const [querySearch, setQuerySearch] = useState("");
  const [resultsClients, setResultsClients] = useState([]);
  const [resultsContacts, setResultsContacts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [openMobileSearch, setOpenMobileSearch] = useState(false);

  const { unreadCount, unreadItems, refreshUnread } = useUnreadMessages();

  const searchContainerRef = useRef(null);
  const resultListRef = useRef(null);
  const { logout } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isReportsHeader = location.pathname.startsWith("/informes");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { origin } = window.location;

  // ----------------- Debounce búsqueda -----------------
  useEffect(() => {
    const searchTerm = querySearch.trim();

    if (!searchTerm || searchTerm.length < 3) {
      setResultsClients([]);
      setResultsContacts([]);
      return;
    }

    const handler = setTimeout(async () => {
      setIsSearching(true);
      try {
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/searchcontactsclientsinfo?search=${encodeURIComponent(searchTerm)}`,
        );
        setResultsClients(data.resultsClients || []);
        setResultsContacts(data.resultsContacts || []);
      } catch (error) {
        console.error("Error en la búsqueda:", error);
        setResultsClients([]);
        setResultsContacts([]);
      } finally {
        setIsSearching(false);
      }
    }, 500); // 0.5s debounce

    return () => clearTimeout(handler);
  }, [querySearch]);
  // -------------------------------------------------------

  // Cerrar resultados al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setQuerySearch("");
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleOpenMailMenu = (event) => {
    setMailAnchorEl(event.currentTarget);
    refreshUnread();
  };
  const handleCloseMailMenu = () => setMailAnchorEl(null);

  const handleOpenNotificationMessage = async (item) => {
    try {
      if (item?.id) {
        await api.post(`${process.env.REACT_APP_API_URL}/messages/mark-read`, {
          id: item.id,
        });
        window.dispatchEvent(new Event("messages:marked-read"));
      }
    } catch (error) {
      console.error("No se pudo actualizar el estado del mensaje", error);
    } finally {
      handleCloseMailMenu();
      navigate(`/mensajes?message_id=${item?.id || ""}`);
    }
  };

  const cerrarSesion = () => {
    logout(true);
  };

  return (
    <div className="root">
      <AppBar
        position="static"
        color="default"
        sx={{
          boxShadow: isReportsHeader
            ? "0px 4px 10px rgba(0,0,0,0.2)"
            : "0px 4px 10px rgba(0,0,0,0.1)",
          background: isReportsHeader
            ? "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(2,6,23,0.96))"
            : "#ffffff",
          borderBottom: isReportsHeader
            ? "1px solid rgba(96,165,250,0.18)"
            : "1px solid rgba(226,232,240,0.8)",
        }}
      >
        <Toolbar
          sx={{
            px: "10px !important",
            background: isReportsHeader ? "rgba(15, 23, 42, 0.72)" : "#ffffff",
            minHeight: 68,
          }}
        >
          {/* HAMBURGUESA MOBILE */}
          {isMobile && (
            <IconButton
              onClick={() => handleToggleSidebar(true)}
              sx={{
                color: isReportsHeader ? "#e2e8f0" : "inherit",
                "&:hover": {
                  backgroundColor: isReportsHeader
                    ? "rgba(148,163,184,0.1)"
                    : undefined,
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* ICONO SEARCH MOBILE */}
          {isMobile && (
            <IconButton
              onClick={() => setOpenMobileSearch((prev) => !prev)}
              sx={{
                color: isReportsHeader ? "#e2e8f0" : "inherit",
                "&:hover": {
                  backgroundColor: isReportsHeader
                    ? "rgba(148,163,184,0.1)"
                    : undefined,
                },
              }}
            >
              <SearchIcon />
            </IconButton>
          )}

          {/* SEARCH DESKTOP / MOBILE */}
          {(!isMobile || openMobileSearch) && (
            <Box
              ref={searchContainerRef}
              className="title containerGeneralSearch"
              sx={{ flex: 1, position: "relative" }}
            >
              {/* Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  background: isReportsHeader
                    ? "linear-gradient(135deg, rgba(15,23,42,0.95), rgba(15,23,42,0.82))"
                    : "#f4f6f8",
                  borderRadius: "10px",
                  px: 1.5,
                  py: 0.5,
                  border: "1px solid",
                  borderColor: isReportsHeader
                    ? querySearch
                      ? "#60a5fa"
                      : "rgba(148,163,184,0.22)"
                    : querySearch
                      ? "#1976d2"
                      : "transparent",
                  boxShadow: isReportsHeader
                    ? querySearch
                      ? "0 0 0 1px rgba(96,165,250,0.35), 0 8px 28px rgba(59,130,246,0.2)"
                      : "0 8px 24px rgba(15,23,42,0.16)"
                    : "none",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  m: isMobile ? "0px" : "0px 15px 0px 0px",
                }}
              >
                <SearchIcon
                  sx={{
                    color: isReportsHeader ? "#93c5fd" : "#9e9e9e",
                    fontSize: 20,
                    mr: 1,
                  }}
                />
                <InputBase
                  placeholder="Buscar por nombre, documento (NIF, DNI...), teléfono o email…"
                  onChange={(e) => setQuerySearch(e.target.value)}
                  value={querySearch}
                  fullWidth
                  autoComplete="off"
                  sx={{
                    fontSize: 14,
                    color: isReportsHeader ? "#e2e8f0" : "#1f2937",
                    "& input::placeholder": {
                      color: isReportsHeader ? "#94a3b8" : "#6b7280",
                    },
                  }}
                />
                {querySearch && (
                  <IconButton size="small" onClick={() => setQuerySearch("")}>
                    <Close
                      sx={{
                        fontSize: 16,
                        color: isReportsHeader ? "#cbd5e1" : "#9e9e9e",
                      }}
                    />
                  </IconButton>
                )}
              </Box>

              {/* Spinner line */}
              {isSearching && (
                <Box
                  sx={{
                    height: 2,
                    background:
                      "linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.2s infinite linear",
                    borderRadius: 1,
                    mt: "2px",
                    "@keyframes shimmer": {
                      "0%": { backgroundPosition: "200% 0" },
                      "100%": { backgroundPosition: "-200% 0" },
                    },
                  }}
                />
              )}

              {/* Resultados */}
              {!isSearching &&
                (resultsClients.length > 0 || resultsContacts.length > 0) && (
                  <Fade in timeout={200}>
                    <Box
                      ref={resultListRef}
                      sx={{
                        position: "absolute",
                        width: "100%",
                        bgcolor: "rgba(15, 23, 42, 0.97)",
                        boxShadow: "0 16px 34px rgba(15,23,42,0.45)",
                        zIndex: 1300,
                        mt: "4px",
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: "1px solid rgba(96,165,250,0.28)",
                      }}
                    >
                      {/* Clientes */}
                      {resultsClients.length > 0 && (
                        <>
                          <Box
                            sx={{
                              px: 2,
                              py: 0.8,
                              bgcolor: "rgba(59,130,246,0.12)",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <PersonOutline
                              sx={{ fontSize: 15, color: "#93c5fd" }}
                            />
                            <Box
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#93c5fd",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Clientes
                            </Box>
                            <Chip
                              label={resultsClients.length}
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: 10,
                                ml: "auto",
                                bgcolor: "#e3f2fd",
                                color: "#1976d2",
                              }}
                            />
                          </Box>
                          {resultsClients.map((result) => (
                            <ResultRow
                              key={`cliente-${result.id}`}
                              result={result}
                              type="Cliente"
                              href={`/clientes/ver?id=${result.id}`}
                              onClick={() => {
                                setQuerySearch("");
                                navigate(`/clientes/ver?id=${result.id}`);
                              }}
                            />
                          ))}
                        </>
                      )}

                      {/* Separador */}
                      {resultsClients.length > 0 &&
                        resultsContacts.length > 0 && <Divider />}

                      {/* Contactos */}
                      {resultsContacts.length > 0 && (
                        <>
                          <Box
                            sx={{
                              px: 2,
                              py: 0.8,
                              bgcolor: "rgba(168,85,247,0.12)",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Contacts sx={{ fontSize: 15, color: "#d8b4fe" }} />
                            <Box
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#d8b4fe",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Contactos
                            </Box>
                            <Chip
                              label={resultsContacts.length}
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: 10,
                                ml: "auto",
                                bgcolor: "#f3e5f5",
                                color: "#7b1fa2",
                              }}
                            />
                          </Box>
                          {resultsContacts.map((result) => (
                            <ResultRow
                              key={`contacto-${result.id}`}
                              result={result}
                              type="Contacto"
                              href={`/contactos/ver?id=${result.id}`}
                              onClick={() => {
                                setQuerySearch("");
                                navigate(`/contactos/ver?id=${result.id}`);
                              }}
                            />
                          ))}
                        </>
                      )}
                    </Box>
                  </Fade>
                )}

              {/* Sin resultados */}
              {!isSearching &&
                querySearch.trim().length >= 3 &&
                resultsClients.length === 0 &&
                resultsContacts.length === 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      width: "100%",
                      bgcolor: "rgba(15, 23, 42, 0.97)",
                      boxShadow: "0 16px 32px rgba(15,23,42,0.4)",
                      zIndex: 1300,
                      mt: "4px",
                      borderRadius: "10px",
                      py: 2,
                      border: "1px solid rgba(96,165,250,0.2)",
                      fontSize: 13,
                      color: "#cbd5e1",
                      textAlign: "center",
                    }}
                  >
                    Sin resultados para "<strong>{querySearch}</strong>"
                  </Box>
                )}
            </Box>
          )}

          {/* ICONOS DERECHA */}
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              background: isReportsHeader
                ? "rgba(15, 23, 42, 0.58)"
                : "transparent",
              border: isReportsHeader
                ? "1px solid rgba(148,163,184,0.12)"
                : "none",
              borderRadius: "10px",
              px: isReportsHeader ? 1 : 0,
              py: isReportsHeader ? 0.5 : 0,
            }}
          >
            {!isMobile && (
              <Tooltip title="Mensajes">
                <IconButton
                  sx={{
                    mr: 2,
                    color: isReportsHeader ? "#e2e8f0" : "inherit",
                    "&:hover": {
                      backgroundColor: isReportsHeader
                        ? "rgba(148,163,184,0.1)"
                        : undefined,
                    },
                  }}
                  onClick={handleOpenMailMenu}
                >
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    max={99}
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: isReportsHeader
                          ? "#ef4444"
                          : undefined,
                      },
                    }}
                  >
                    <MailIcon
                      sx={{ color: isReportsHeader ? "#e2e8f0" : undefined }}
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            <Menu
              id="mail-notifications-menu"
              anchorEl={mailAnchorEl}
              open={openMailMenu}
              onClose={handleCloseMailMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  width: 380,
                  maxWidth: "95vw",
                  mt: 1,
                  borderRadius: "12px",
                  boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.2, borderBottom: "1px solid #eef1f4" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  Notificaciones de mensajes
                </Typography>
              </Box>

              {unreadItems.length === 0 ? (
                <Box sx={{ px: 2, py: 2 }}>
                  <Typography sx={{ fontSize: 13, color: "#7a8691" }}>
                    No tienes mensajes sin leer.
                  </Typography>
                </Box>
              ) : (
                unreadItems.map((item) => (
                  <MenuItem
                    key={item.id}
                    onClick={() => handleOpenNotificationMessage(item)}
                    sx={{
                      alignItems: "flex-start",
                      whiteSpace: "normal",
                      py: 1.1,
                      borderBottom: "1px solid #f3f5f7",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.4,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#1f2933",
                          lineHeight: 1.3,
                        }}
                      >
                        {item?.subject || "Nuevo mensaje"}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#8292a1" }}>
                        {item?.date || ""}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}

              <Box sx={{ p: 1 }}>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    handleCloseMailMenu();
                    navigate("/mensajes");
                  }}
                >
                  Ver todos los mensajes
                </Button>
              </Box>
            </Menu>
            {/* <Tooltip title="Esta función se encuentra deshabilitada temporalmente">
              <span>
                <IconButton sx={{ mr: 2 }} disabled>
                  <Badge badgeContent={17} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </span>
            </Tooltip> */}
            <IconButton
              onClick={handleMenu}
              sx={{
                color: isReportsHeader ? "#e2e8f0" : "inherit",
                "&:hover": {
                  backgroundColor: isReportsHeader
                    ? "rgba(148,163,184,0.1)"
                    : undefined,
                },
              }}
            >
              <AccountCircle
                sx={{ color: isReportsHeader ? "#e2e8f0" : undefined }}
              />
            </IconButton>
          </Box>

          {/* MINI MENU */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            open={open}
            onClose={handleClose}
          >
            <MenuItem onClick={handleClose}>
              <NavLink to="/perfil">
                <Button color="info">Mi Perfil</Button>
              </NavLink>
            </MenuItem>
            <MenuItem onClick={handleClose}>
              <NavLink to="/perfil">
                <Button>Mensajes</Button>
              </NavLink>
            </MenuItem>
            <MenuItem onClick={handleClose}>
              <NavLink to="/perfil">
                <Button>Notificaciones</Button>
              </NavLink>
            </MenuItem>
            <MenuItem onClick={cerrarSesion}>
              <Button color="error">Cerrar Sesión</Button>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
    </div>
  );
};

export default Header;
